/**
 * EntityBadgeById — hydration wrapper that resolves a single entity id
 * from the CRM store and renders an EntityBadge with click translation.
 *
 * Callers only need to supply an `entityId` and `orgId`. The wrapper
 * handles store subscription, loading/failed state, and navigating to
 * the entity slideout or detail page on click.
 *
 * Do NOT pass a `data` prop — use `EntityBadge` directly if you already
 * have the resolved data.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import {
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';
import { entityTypeToRouteSlug } from '@/lib/crm/utils/entityTypeUtils';

import { buildEntityBadgeData } from './entityBadgeHelpers';
import { HoverPreviewBadge } from './HoverPreviewBadge';
import type {
  ClickBehavior,
  EntityBadgeState,
  EntityBadgeVariant,
} from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EntityBadgeByIdProps {
  entityId: string;
  orgId: number;
  variant?: EntityBadgeVariant;
  avatar?: boolean;
  /**
   * When `false`, omit the leading avatar/icon from the underlying badge.
   * Defaults to `true`. Useful when surrounding context already shows the
   * provider/icon.
   */
  showLeading?: boolean;
  interactive?: boolean;
  disabled?: boolean;
  /** How a click on the badge should navigate. Defaults to `'slideout'`. */
  clickBehavior?: ClickBehavior;
  /**
   * When supplied, overrides `clickBehavior` — the badge calls this
   * directly instead of performing default navigation.
   */
  onClick?: () => void;
  /** Additional className applied to the underlying badge. */
  className?: string;
  /**
   * Display name to use when the entity fetch fails. When supplied, the
   * failed badge renders as a non-interactive gray pill instead of the red
   * "Unavailable" pill. Used by markdown rendering, where the original
   * `<entity>` tag carries a human-readable name and type even when the
   * entity cannot be hydrated from the CRM store.
   */
  fallbackDisplayName?: string;
  /**
   * Entity type name (e.g. `ahoy_deal`) used alongside `fallbackDisplayName`
   * for type-aware avatar/icon rendering. Optional — defaults to a benign
   * placeholder when omitted. Used by markdown rendering, where the original
   * `<entity>` tag carries a human-readable name and type even when the
   * entity cannot be hydrated from the CRM store.
   */
  fallbackEntityType?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EntityBadgeById({
  entityId,
  orgId,
  variant = 'default',
  avatar = false,
  showLeading = true,
  interactive = true,
  disabled = false,
  clickBehavior = 'slideout',
  onClick,
  className,
  fallbackDisplayName,
  fallbackEntityType,
}: EntityBadgeByIdProps): React.ReactElement {
  const object = useCrmObjectSnapshot(entityId, orgId);

  // Subscribe to fetch failure changes so the component re-renders when a
  // fetch fails, transitioning from `loading` → `failed` state.
  useSyncExternalStore(subscribeToFetchFailures, getFailureVersion);

  const openSlideout = useOpenSlideoutOptional();
  const navigate = useNavigate();

  const handleDefaultClick = useCallback(() => {
    if (clickBehavior === 'slideout') {
      openSlideout?.(entityId);
    } else if (clickBehavior === 'page' && object) {
      navigate(
        `/${orgId}/crm/${entityTypeToRouteSlug(object.entity_type_name)}/${entityId}`
      );
    }
  }, [clickBehavior, openSlideout, entityId, navigate, orgId, object]);

  // Build the render state. Treat empty/whitespace-only fallback names as
  // missing so the failed state renders the red "Unavailable" pill instead
  // of an unlabeled gray pill.
  const trimmedFallbackName = fallbackDisplayName?.trim();
  let state: EntityBadgeState;
  if (object) {
    state = { kind: 'data', data: buildEntityBadgeData(object) };
  } else if (hasFailedFetch(entityId)) {
    state = trimmedFallbackName
      ? {
          kind: 'failed',
          fallbackData: {
            id: entityId,
            entityType: fallbackEntityType ?? '',
            displayName: trimmedFallbackName,
          },
        }
      : { kind: 'failed' };
  } else {
    state = { kind: 'loading' };
  }

  // Resolve which onClick to pass to the primitive.
  let resolvedOnClick: (() => void) | undefined;
  if (onClick) {
    resolvedOnClick = onClick;
  } else if (clickBehavior !== 'none') {
    resolvedOnClick = handleDefaultClick;
  }

  return (
    <HoverPreviewBadge
      entityId={entityId}
      orgId={orgId}
      entityType={object?.entity_type_name}
      state={state}
      variant={variant}
      avatar={avatar}
      showLeading={showLeading}
      interactive={interactive}
      disabled={disabled}
      onClick={resolvedOnClick}
      className={className}
    />
  );
}
